import React from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import ModalUnstyled from '@mui/base/ModalUnstyled';
import clsx from 'clsx';

import twclsx from 'utils/twclsx';

const backdropStyles = 'inset-0 z-20 fixed h-full w-full overflow-y-auto bg-black/50';

/**
 * Custom backdrop component for the Modal
 * Fixes ownerState error (see: https://github.com/mui/material-ui/issues/32882#issuecomment-1135784140)
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} props.open - Whether the backdrop is open
 * @param {Object} [props.ownerState] - State passed from MUI
 * @param {React.Ref} ref - Forwarded ref
 * @returns {JSX.Element} Backdrop component
 */
const Backdrop = React.forwardRef((props, ref) => {
    // eslint-disable-next-line no-unused-vars
    const { className = null, open, ownerState = null, ...other } = props;

    return <div className={clsx('MuiModal-backdrop', { 'MuiBackdrop-open': open }, className)} {...other} ref={ref} />;
});

/**
 * Modal component that displays content in an overlay
 *
 * @param {Object} props - Component props
 * @param {string} [props.bgColor='bg-white'] - Background color for the modal (Tailwind class)
 * @param {boolean} [props.centered=false] - Whether to center content vertically within the modal
 * @param {React.ReactNode} props.children - Content to be displayed inside the modal
 * @param {boolean} [props.flush=false] - Whether to disable overflow scrolling
 * @param {string|React.ReactNode} [props.headline=null] - Headline text or component to display at the top
 * @param {React.ReactNode} [props.icon=null] - Optional icon to display at the top of the modal
 * @param {boolean} [props.includeHorizontalPadding=true] - Whether to add horizontal padding
 * @param {Function} props.onClose - Function called when the modal is closed
 * @param {boolean} props.open - Whether the modal is open
 * @param {boolean} [props.topGradientBorder=false] - Whether to display a gradient border at the top
 * @param {boolean} [props.xToClose=true] - Whether to show an X button to close the modal
 * @param {Object} [props.styles={}] - Custom styles object for the modal
 * @param {string} [props.styles.contentRoot] - Additional CSS classes for the modal's root content container
 * @returns {JSX.Element} Modal component
 */
function Modal({
    bgColor = 'bg-white',
    centered = false,
    children,
    flush = false,
    headline = null,
    icon = null,
    includeHorizontalPadding = true,
    onClose,
    open,
    styles = {},
    topGradientBorder = false,
    xToClose = true,
    ...rest
}) {
    /**
     * Handles the close button click event
     *
     * @param {React.SyntheticEvent} e - The event object
     */
    const handleCloseButtonClick = (e) => {
        e.stopPropagation();
        if (e.type !== 'keydown' || e.code === 'Enter' || e.key === 'Enter' || e.keyCode === 13) onClose(e);
    };

    return (
        <ModalUnstyled
            open={open}
            onClose={onClose}
            slots={{ backdrop: Backdrop }}
            slotProps={{
                backdrop: {
                    className: backdropStyles,
                },
            }}
            className="fixed inset-0 z-5000 flex md:items-center md:justify-center"
            {...rest}
        >
            <div
                className={twclsx(
                    bgColor,
                    'md:shadow-base relative z-40 flex grow flex-col overflow-hidden pb-30 md:max-h-[90%] md:max-w-[640px] md:rounded-md lg:max-w-[800px]',
                    { 'px-10 md:px-20': includeHorizontalPadding },
                    { 'flex items-center': centered },
                    { 'pt-50': xToClose },
                    { 'pt-10 md:pt-20': !xToClose },
                    styles.contentRoot
                )}
                data-testid="modal-content-root"
            >
                {topGradientBorder && <div className="bg-gradient-berry-blend absolute top-0 left-0 h-[8px] w-full" />}
                {xToClose && (
                    <button
                        type="button"
                        onKeyDown={handleCloseButtonClick}
                        onClick={handleCloseButtonClick}
                        className="absolute top-15 right-10 rounded-full bg-white md:right-20"
                        aria-label="Close modal"
                    >
                        <XMarkIcon className="h-30 w-30 text-black" />
                    </button>
                )}
                {icon && icon}
                {headline &&
                    (typeof headline === 'string' ? (
                        <h2 className={clsx(['text-heading-md mb-20', { 'text-center': centered }])}>{headline}</h2>
                    ) : (
                        <header className={clsx('mb-5', { 'text-center': centered })}>{headline}</header>
                    ))}
                <div
                    className={clsx(
                        { 'overflow-y-auto': !flush },
                        { 'flex min-h-0 flex-1 flex-col overflow-hidden': flush }
                    )}
                >
                    {children}
                </div>
            </div>
        </ModalUnstyled>
    );
}

export default Modal;
